import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Input,
  Spin,
  Tabs,
  Upload,
  Button,
  message,
  Form,
  DatePicker,
  Row,
  Col,
} from "antd";
import {
  UploadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "./refund.scss";

const BASE_URL = process.env.REACT_APP_CSC_API_BASE_URL;
const { TabPane } = Tabs;

const columns = [
  {
    title: "#",
    dataIndex: "index",
    key: "index",
    width: 50,
    align: "center",
  },
  {
    title: "Merchant ID",
    dataIndex: ["requestBody", "merchant_id"],
    key: "merchant_id",
  },
  {
    title: "Merchant Txn",
    dataIndex: ["requestBody", "merchant_txn"],
    key: "merchant_txn",
  },
  {
    title: "CSC Txn",
    dataIndex: ["requestBody", "csc_txn"],
    key: "csc_txn",
  },
  {
    title: "Refund Reason",
    dataIndex: ["requestBody", "refund_reason"],
    key: "refund_reason",
  },
  {
    title: "Refund Type",
    dataIndex: ["requestBody", "refund_type"],
    key: "refund_type",
  },
  {
    title: "Refund Trigger",
    dataIndex: ["requestBody", "refund_trigger"],
    key: "refund_trigger",
  },
  {
    title: "Refund Status",
    dataIndex: ["responseBody", "walletResponseData", "map", "refund_status"],
    key: "refund_status",
  },
  {
    title: "Refund Reference",
    dataIndex: ["responseBody", "walletResponseData", "map", "refund_reference"],
    key: "refund_reference",
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (text) => moment(text).format("DD-MM-YYYY HH:mm:ss"),
  },
];


const RefundRequest = () => {
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [tableParams, setTableParams] = useState({
    current: 1,
    pageSize: 10,
  });
  const [search, setSearch] = useState("");
  const searchRef = useRef();

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [uploading, setUploading] = useState(false);

  const [form] = Form.useForm();
  const [singleLoading, setSingleLoading] = useState(false);

  const token = sessionStorage.getItem("biller_access");

  const fetchRefundData = async (
    page = 1,
    pageSize = 10,
    searchText = "",
    startDate = fromDate,
    endDate = toDate
  ) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}api/cou/refund`, {
        params: {
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? endDate.toISOString() : undefined,
          search: searchText,
          page,
          limit: pageSize,
        },
      });
      const refundData = res.data.payload || [];

      console.log(res, "refundData");
      setData(
        refundData.map((item, idx) => ({
          ...item,
          index: (page - 1) * pageSize + idx + 1,
          requestBody: item.requestBody || {},
          responseBody: item.responseBody || {},
        }))
      );
      setTotalData(res.data.totalData || 0);
    } catch (err) {
      setData([]);
      setTotalData(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefundData(
      tableParams.current,
      tableParams.pageSize,
      search,
      fromDate,
      toDate
    );
    // eslint-disable-next-line
  }, [tableParams.current, tableParams.pageSize, search, fromDate, toDate]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      setSearch(value);
      setTableParams((prev) => ({ ...prev, current: 1 }));
    }, 500);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    if (date && toDate && date > toDate) setToDate(null);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    if (date && fromDate && date < fromDate) setFromDate(null);
  };

  const disabledFromDate = (current) => {
    if (!current) return false;
    if (toDate && current > toDate.endOf("day")) return true;
    return current > moment().endOf("day");
  };

  const disabledToDate = (current) => {
    if (!current) return false;
    if (fromDate && current < fromDate.startOf("day")) return true;
    return current > moment().endOf("day");
  };

  const disabledTime = (currentDate) => {
    const now = moment();
    if (!currentDate) return {};
    if (currentDate.isSame(now, "day")) {
      return {
        disabledHours: () =>
          Array.from({ length: 24 }, (_, i) => i).filter(
            (hour) => hour > now.hour()
          ),
        disabledMinutes: (selectedHour) => {
          if (selectedHour === now.hour()) {
            return Array.from({ length: 60 }, (_, i) => i).filter(
              (minute) => minute > now.minute()
            );
          }
          return [];
        },
        disabledSeconds: () => [],
      };
    }
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
      disabledSeconds: () => [],
    };
  };

  const handleTableChange = (pagination) => {
    setTableParams({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const customUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      await axios.post(
        `${BASE_URL}api/dashboard/refund/bulk-upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } }
      );
      onSuccess();
      message.success("Bulk refund upload successful!");
      fetchRefundData(
        tableParams.current,
        tableParams.pageSize,
        search,
        fromDate,
        toDate
      );
    } catch (err) {
      onError();
      message.error("Bulk refund upload failed!");
    } finally {
      setUploading(false);
    }
  };

  const handleSingleRefund = async (values) => {
    setSingleLoading(true);
    try {
      await axios.post(`${BASE_URL}api/dashboard/refund`, values,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Refund processed successfully!");
      form.resetFields();
      fetchRefundData(
        tableParams.current,
        tableParams.pageSize,
        search,
        fromDate,
        toDate
      );
    } catch (err) {
      message.error("Refund failed!");
    } finally {
      setSingleLoading(false);
    }
  };

  return (
    <div className="mainContain" style={{ maxWidth: 1200, margin: "0 auto", padding: 24 }}>
      <Tabs defaultActiveKey="1">
        <TabPane tab="Upload Refund Excel" key="1">
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Upload.Dragger
                name="file"
                customRequest={customUpload}
                showUploadList={false}
                accept=".xlsx,.xls"
                disabled={uploading}
                style={{ marginBottom: 24, padding: 24, background: "#fafafa", borderRadius: 8 }}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
                <p className="ant-upload-hint">Supports only .xlsx/.xls files</p>
                {uploading && <Spin style={{ marginTop: 16 }} />}
              </Upload.Dragger>
            </Col>
            <Col xs={24} md={12}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSingleRefund}
                style={{ maxWidth: 400, margin: "0 auto", marginTop: 32 }}
              >
                <Form.Item
                  label="Merchant Transaction ID"
                  name="merchant_txn"
                  rules={[{ required: true, message: "Please enter Merchant Transaction ID" }]}
                >
                  <Input placeholder="Enter Merchant Transaction ID" />
                </Form.Item>
                <Form.Item
                  label="CSC Transaction ID"
                  name="csc_txn"
                  rules={[{ required: true, message: "Please enter CSC Transaction ID" }]}
                >
                  <Input placeholder="Enter CSC Transaction ID" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={singleLoading}>
                    Submit Refund
                  </Button>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </TabPane>
        <TabPane tab="Refund List" key="2">
          <div style={{ marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Input.Search
              placeholder="Search"
              allowClear
              value={searchInput}
              onChange={handleSearch}
              style={{ width: 250 }}
            />
            <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              <DatePicker
                value={fromDate}
                style={{ width: 220, border: "none" }}
                onChange={handleFromDateChange}
                disabledDate={disabledFromDate}
                format="DD-MM-YYYY HH:mm:ss"
                showTime={{
                  format: "HH:mm:ss",
                  disabledTime: disabledTime,
                }}
                showNow={false}
                allowClear
                placeholder="From Date (DD-MM-YYYY HH:mm:ss)"
                suffixIcon={<CalendarOutlined style={{ color: "#d9d3d3" }} />}
              />
              <DatePicker
                value={toDate}
                style={{ width: 220, border: "none" }}
                onChange={handleToDateChange}
                disabledDate={disabledToDate}
                format="DD-MM-YYYY HH:mm:ss"
                showTime={{
                  format: "HH:mm:ss",
                  disabledTime: disabledTime,
                }}
                showNow={false}
                allowClear
                placeholder="To Date (DD-MM-YYYY HH:mm:ss)"
                suffixIcon={<CalendarOutlined style={{ color: "#d9d3d3" }} />}
              />
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="_id"
            pagination={{
              total: totalData,
              current: tableParams.current,
              pageSize: tableParams.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ["10", "20", "50", "100", "200"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={handleTableChange}
          />
          <Spin
            spinning={loading}
            tip="Loading..."
            style={{
              display: loading ? "flex" : "none",
              justifyContent: "center",
              marginTop: "20px",
            }}
            className="loading-spinner"
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default RefundRequest;
